import React, { useState, useEffect } from 'react';
import { Stethoscope, Settings as SettingsIcon, AlertCircle, Users, LogOut } from 'lucide-react';
import { ChatInput } from './components/ChatInput';
import { ConversationHistory } from './components/ConversationHistory';
import { SettingsPanel } from './components/SettingsPanel';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AdminPanel } from './components/AdminPanel';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { anonymizeText, DEFAULT_ANONYMIZATION_CONFIG } from './utils/anonymization';
import { cloudStorageService } from './services/cloudStorage';
import { apiService } from './services/api';
import { ConversationEntry, ConversationExchange, AppSettings, LoadingState } from './types';

const getDefaultSettings = (): AppSettings => ({
  aiProvider: 'openai' as const,
  openai: {
    apiKey: '', // API keys now handled by backend
    model: 'gpt-4o-mini',
  },
  anonymizationConfig: DEFAULT_ANONYMIZATION_CONFIG,
});

function MainApp() {
  const { user, logout } = useAuth();
  const [currentConversation, setCurrentConversation] = useState<ConversationEntry | null>(null);
  const [conversationHistory, setConversationHistory] = useState<ConversationEntry[]>([]);
  const [settings, setSettings] = useState<AppSettings>(getDefaultSettings());
  const [showSettings, setShowSettings] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [loading, setLoading] = useState<LoadingState>({
    isAnalyzing: false,
    isLoadingConversations: true,
  });
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const savedSettings = localStorage.getItem('soap-refiner-settings');
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        const defaultSettings = getDefaultSettings();
        setSettings({ ...defaultSettings, ...parsed });
      } catch (e) {
        console.error('Failed to parse saved settings:', e);
      }
    }

    // Load conversations from cloud storage and handle migration
    const loadConversations = async () => {
      try {
        setLoading(prev => ({ ...prev, isLoadingConversations: true }));
        
        // Check if there's localStorage data to migrate
        const savedConversations = localStorage.getItem('soap-refiner-conversations');
        if (savedConversations) {
          console.log('Found localStorage data, migrating to cloud storage...');
          await cloudStorageService.migrateLocalStorageData();
        }
        
        // Load conversations from cloud storage
        const conversations = await cloudStorageService.loadConversations();
        setConversationHistory(conversations);
      } catch (error) {
        console.error('Failed to load conversations:', error);
        setError('Failed to load conversation history. Please try refreshing the page.');
      } finally {
        setLoading(prev => ({ ...prev, isLoadingConversations: false }));
      }
    };

    loadConversations();
  }, []);

  useEffect(() => {
    localStorage.setItem('soap-refiner-settings', JSON.stringify(settings));
  }, [settings]);

  // Save current conversation to cloud storage when it changes
  useEffect(() => {
    if (currentConversation && currentConversation.exchanges.length > 0) {
      const saveConversation = async () => {
        try {
          await cloudStorageService.saveConversation(currentConversation);
        } catch (error) {
          console.error('Failed to save conversation to cloud:', error);
          // Don't show error to user for now, as conversation is still in memory
        }
      };
      
      // Debounce saves to avoid too many API calls
      const timeout = setTimeout(saveConversation, 1000);
      return () => clearTimeout(timeout);
    }
  }, [currentConversation]);


  const detectSoapNote = (text: string): boolean => {
    const soapKeywords = ['subjective', 'objective', 'assessment', 'plan', 'chief complaint', 'hpi', 'ros', 'physical exam', 'vital signs', 'bp:', 'hr:', 'temp:', 'cc:', 's:', 'o:', 'a:', 'p:'];
    const lowerText = text.toLowerCase();
    const wordCount = text.trim().split(/\s+/).length;
    
    // Check if text contains SOAP-related keywords and is substantial
    const hasKeywords = soapKeywords.some(keyword => lowerText.includes(keyword));
    const isSubstantial = wordCount > 20; // Assume SOAP notes are more detailed
    
    return hasKeywords && isSubstantial;
  };

  const handleChatSubmit = async (text: string, messageType: 'soap_note' | 'chat') => {
    setError('');

    // Auto-detect SOAP note content
    const isSoapNote = detectSoapNote(text);
    const actualMessageType = isSoapNote ? 'soap_note' : 'chat';
    let anonymizationResult: any = { anonymizedText: text, replacements: [] };
    
    if (isSoapNote) {
      anonymizationResult = anonymizeText(text, settings.anonymizationConfig);
    }

    // Create temporary exchange with user input (no analysis yet)
    const tempExchange: ConversationExchange = {
      id: Date.now().toString(),
      timestamp: new Date(),
      originalText: text,
      anonymizedText: anonymizationResult.anonymizedText,
      analysis: '', // Empty initially
      replacements: anonymizationResult.replacements,
      messageType: actualMessageType,
    };

    // Immediately show user input
    if (currentConversation) {
      setCurrentConversation(prev => ({
        ...prev!,
        lastUpdated: new Date(),
        exchanges: [tempExchange, ...prev!.exchanges],
        conversationType: prev!.conversationType === 'soap_notes' ? 'mixed' : 'chat'
      }));
    } else {
      const newConversation: ConversationEntry = {
        id: Date.now().toString(),
        startedAt: new Date(),
        lastUpdated: new Date(),
        exchanges: [tempExchange],
        title: `${actualMessageType === 'soap_note' ? 'SOAP Note' : 'Chat'} Conversation ${new Date().toLocaleDateString()}`,
        conversationType: actualMessageType === 'soap_note' ? 'soap_notes' : 'chat'
      };
      setCurrentConversation(newConversation);
    }

    setLoading({ isAnalyzing: true });

    try {
      // Use backend API for AI analysis
      const textToAnalyze = isSoapNote ? anonymizationResult.anonymizedText : text;
      const result = await apiService.analyzeText(textToAnalyze, actualMessageType, settings.aiProvider);
      
      // Update the exchange with the analysis
      const completedExchange: ConversationExchange = {
        ...tempExchange,
        analysis: result.analysis,
      };

      setCurrentConversation(prev => ({
        ...prev!,
        lastUpdated: new Date(),
        exchanges: prev!.exchanges.map(ex => 
          ex.id === tempExchange.id ? completedExchange : ex
        )
      }));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      // Remove the temporary exchange on error
      setCurrentConversation(prev => prev ? {
        ...prev,
        exchanges: prev.exchanges.filter(ex => ex.id !== tempExchange.id)
      } : null);
    } finally {
      setLoading({ isAnalyzing: false });
    }
  };

  const handleSettingsSave = (newSettings: AppSettings) => {
    setSettings(newSettings);
    setError('');
  };

  const handleExport = () => {
    // Export functionality removed - now handled by Previous Conversations modal
  };

  const handleClearCurrent = async () => {
    if (window.confirm('Are you sure you want to clear the current conversation? This cannot be undone.')) {
      if (currentConversation) {
        try {
          // Save current conversation to history in cloud storage first
          await cloudStorageService.saveConversation(currentConversation);
          // Reload conversations to get updated list
          const conversations = await cloudStorageService.loadConversations();
          setConversationHistory(conversations);
        } catch (error) {
          console.error('Failed to save current conversation:', error);
          setError('Failed to save conversation. Please try again.');
          return;
        }
      }
      setCurrentConversation(null);
    }
  };

  const handleClearHistory = async () => {
    if (window.confirm('Are you sure you want to clear all conversation history? This cannot be undone.')) {
      try {
        // Delete all conversations from cloud storage
        for (const conversation of conversationHistory) {
          await cloudStorageService.deleteConversation(conversation.id);
        }
        setConversationHistory([]);
        setCurrentConversation(null);
      } catch (error) {
        console.error('Failed to clear conversation history:', error);
        setError('Failed to clear conversation history. Please try again.');
      }
    }
  };

  // API keys are now handled by backend, so always consider configured
  const isConfigured = true;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-medical-100 p-2 rounded-lg">
                <Stethoscope className="h-8 w-8 text-medical-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Vivaria</h1>
                <p className="text-sm text-gray-600">AI-Powered Medical Documentation Assistant</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="text-right mr-3">
                <p className="text-sm font-medium text-gray-900">{user?.username}</p>
                <p className="text-xs text-gray-600">
                  {user?.id === 1 ? 'Administrator' : 'User'}
                </p>
              </div>
              <div className="flex space-x-2">
                {user?.id === 1 && (
                  <button
                    onClick={() => setShowAdminPanel(true)}
                    className="flex items-center space-x-2 bg-blue-100 hover:bg-blue-200 text-blue-700 px-3 py-2 rounded-lg transition-colors"
                  >
                    <Users className="h-4 w-4" />
                    <span>Users</span>
                  </button>
                )}
                <button
                  onClick={() => setShowSettings(true)}
                  className="flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-lg transition-colors"
                >
                  <SettingsIcon className="h-4 w-4" />
                  <span>Settings</span>
                </button>
                <button
                  onClick={logout}
                  className="flex items-center space-x-2 bg-red-100 hover:bg-red-200 text-red-700 px-3 py-2 rounded-lg transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Sign Out</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!isConfigured && (
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <AlertCircle className="h-5 w-5 text-amber-600" />
              <div>
                <h3 className="text-sm font-medium text-amber-800">Configuration Required</h3>
                <p className="text-sm text-amber-700 mt-1">
                  Please configure your {settings.aiProvider === 'openai' ? 'OpenAI' : 'Google Gemini'} API key in settings to start analyzing SOAP notes.
                </p>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <AlertCircle className="h-5 w-5 text-red-600" />
              <div>
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col h-[calc(100vh-280px)]">
            <div className="flex-1 overflow-hidden">
              <ConversationHistory
                currentConversation={currentConversation}
                conversationHistory={conversationHistory}
                onExport={handleExport}
                onClear={handleClearHistory}
                onClearCurrent={handleClearCurrent}
                loading={loading.isLoadingConversations}
              />
            </div>
            
            <div className="mt-4">
              <ChatInput
                onSubmit={handleChatSubmit}
                loading={loading.isAnalyzing}
                placeholder="Message Vivaria - ask questions, share SOAP notes for analysis, or have a conversation..."
              />
            </div>
          </div>
        </div>
      </main>

      <footer className="bg-white border-t border-gray-200 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="text-center text-sm text-gray-500">
            <p>Vivaria - Secure, Privacy-First Medical Documentation Assistant</p>
            <p className="mt-1">All PHI is automatically anonymized before AI analysis. Data is stored locally.</p>
          </div>
        </div>
      </footer>

      <SettingsPanel
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        settings={settings}
        onSave={handleSettingsSave}
      />

      <AdminPanel
        isOpen={showAdminPanel}
        onClose={() => setShowAdminPanel(false)}
      />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <ProtectedRoute>
        <MainApp />
      </ProtectedRoute>
    </AuthProvider>
  );
}

export default App;